import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';

type StripeContextType = {
  isLoading: boolean;
  error: string | null;
};

const StripeContext = createContext<StripeContextType>({
  isLoading: true,
  error: null,
});

export const useStripe = () => useContext(StripeContext);

// Get Stripe publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load Stripe.js
    stripePromise
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load Stripe.js', err);
        setError('Failed to load address validation system. Please try again later.');
        setIsLoading(false);
      });
  }, []);

  // Stripe elements options for address collection (no payment needed)
  const options: StripeElementsOptions = {
    mode: 'setup' as const,
    currency: 'usd',
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#00742f', // jamaica-green
      },
    },
  };

  return (
    <StripeContext.Provider value={{ 
      isLoading, 
      error
    }}>
      <Elements stripe={stripePromise} options={options}>
        {children}
      </Elements>
    </StripeContext.Provider>
  );
}; 